import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AlarmClock,
  ArrowLeft,
  CalendarDays,
  CalendarPlus,
  Check,
  HandHeart,
  MessageSquareReply,
  Users as UsersIcon,
  XCircle,
} from "lucide-react";
import { Avatar, Button, Dialog, Pill, toast } from "../ui";
import CreateMeetingDialog from "./CreateMeetingDialog";
import { useAuth } from "../auth/AuthContext";
import {
  governanceApi,
  errMessage,
  formatDateTime,
  daysUntil,
  ISSUE_KIND_META,
  ISSUE_STATUS_META,
  MEETING_KIND_META,
  MEETING_STATUS_META,
} from "./api";
import TrailTimeline from "./TrailTimeline";
import "./IssueDetailPage.css";

const ANSWER_MIN = 10;
const CLOSE_REASON_MIN = 5;

function stepsForIssue(issue) {
  const terminal = issue.status === "CLOSED" ? "CLOSED" : "RESOLVED";
  let steps;
  switch (issue.kind) {
    case "QUERY":
      steps = ["OPEN", "ANSWERED", "CLOSED"];
      break;
    case "DISPUTE":
      steps = ["OPEN", "IN_MEDIATION", terminal];
      break;
    case "NO_CONFIDENCE":
      steps = ["OPEN", "THRESHOLD_MET", "CLOSED"];
      break;
    default:
      steps = ["OPEN", "THRESHOLD_MET", "SCHEDULED", terminal];
  }
  // If the live status isn't on the canonical path (e.g. a SCHEDULED query),
  // splice it in before the terminal state so the strip never lies.
  if (!steps.includes(issue.status)) {
    steps = [...steps.slice(0, steps.length - 1), issue.status, steps[steps.length - 1]];
  }
  return steps;
}

export default function IssueDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [issue, setIssue] = useState(null);
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState("");
  const [supportBusy, setSupportBusy] = useState(false);
  const [answerText, setAnswerText] = useState("");
  const [answerBusy, setAnswerBusy] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeReason, setCloseReason] = useState("");
  const [closeBusy, setCloseBusy] = useState(false);
  const [conveneOpen, setConveneOpen] = useState(false);
  const [trailKey, setTrailKey] = useState(0);

  const load = useCallback(() => {
    governanceApi
      .getIssue(id)
      .then((d) => {
        setIssue(d);
        setError("");
      })
      .catch((e) => setError(errMessage(e)));
    governanceApi
      .overview()
      .then(setOverview)
      .catch(() => {});
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const apply = (next) => {
    setIssue(next);
    setTrailKey((k) => k + 1);
  };

  if (error && !issue) {
    return (
      <div className="vsgov-shell">
        <BackLink />
        <div className="vsgov-error">{error}</div>
      </div>
    );
  }
  if (!issue) {
    return (
      <div className="vsgov-shell">
        <BackLink />
        <div className="vsgov-loading">Loading issue…</div>
      </div>
    );
  }

  const me = overview?.me || {};
  const kindMeta = ISSUE_KIND_META[issue.kind] || { label: issue.kind, pill: "neutral" };
  const statusMeta =
    ISSUE_STATUS_META[issue.status] || { label: issue.status, pill: "neutral" };
  const steps = stepsForIssue(issue);
  const currentStep = steps.indexOf(issue.status);
  const isTerminal = ["RESOLVED", "CLOSED", "ANSWERED"].includes(issue.status);
  const supportable =
    issue.kind !== "QUERY" && ["OPEN", "THRESHOLD_MET"].includes(issue.status);
  const pct =
    issue.support_needed > 0
      ? Math.min(100, Math.round((issue.support_count / issue.support_needed) * 100))
      : 0;
  const raisedByMe = !!(user?.id && issue.raised_by && issue.raised_by.id === user.id);
  const canClose =
    !["RESOLVED", "CLOSED"].includes(issue.status) &&
    (me.is_board || me.is_elder || raisedByMe);
  const deadlineDays =
    issue.status === "THRESHOLD_MET" && issue.deadline_at
      ? daysUntil(issue.deadline_at)
      : null;
  // A meeting request that cleared 10% support lets its raiser (or the
  // board) convene the owners' meeting — the only point at which the
  // invitation can be sent.
  const canConvene =
    issue.kind === "MEETING_REQUEST" &&
    issue.status === "THRESHOLD_MET" &&
    !issue.meeting &&
    (raisedByMe || me.is_board);

  const toggleSupport = async () => {
    if (supportBusy) return;
    setSupportBusy(true);
    try {
      const next = issue.my_support
        ? await governanceApi.withdrawSupport(issue.id)
        : await governanceApi.supportIssue(issue.id);
      apply(next);
      toast.success(
        issue.my_support ? "Support withdrawn." : "Your support is recorded."
      );
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setSupportBusy(false);
    }
  };

  const submitAnswer = async () => {
    if (answerBusy || answerText.trim().length < ANSWER_MIN) return;
    setAnswerBusy(true);
    try {
      const next = await governanceApi.answerIssue(issue.id, answerText.trim());
      apply({ ...issue, ...next });
      setAnswerText("");
      toast.success("Answer published to the member.");
      load();
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setAnswerBusy(false);
    }
  };

  const submitClose = async () => {
    if (closeBusy || closeReason.trim().length < CLOSE_REASON_MIN) return;
    setCloseBusy(true);
    try {
      const next = await governanceApi.closeIssue(issue.id, closeReason.trim());
      apply({ ...issue, ...next });
      setCloseOpen(false);
      setCloseReason("");
      toast.success("Issue closed.");
      load();
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setCloseBusy(false);
    }
  };

  return (
    <div className="vsgov-shell vsgov-issue-detail">
      <BackLink />

      <div className="vsgov-detail-head">
        <div className="vsgov-card-pills">
          <Pill variant={kindMeta.pill}>{kindMeta.label}</Pill>
          <Pill variant={statusMeta.pill}>{statusMeta.label}</Pill>
        </div>
        <h1>{issue.title}</h1>
        <div className="vsgov-card-meta">
          <Avatar
            name={issue.raised_by?.name || "System"}
            imageUrl={issue.raised_by?.avatar_url}
            size={22}
          />
          <span>
            Raised by {issue.raised_by?.name || "the system"} ·{" "}
            {formatDateTime(issue.created_at)}
          </span>
        </div>
      </div>

      {/* Status timeline strip */}
      <ol className="vsgov-steps" aria-label="Issue progress">
        {steps.map((s, idx) => {
          const meta = ISSUE_STATUS_META[s] || { label: s };
          const done = currentStep >= 0 && idx < currentStep;
          const current = idx === currentStep;
          return (
            <li
              key={s}
              className={`vsgov-step ${done ? "done" : ""} ${current ? "current" : ""}`}
            >
              <span className="vsgov-step-dot">
                {done ? <Check size={11} strokeWidth={3} /> : null}
              </span>
              <span className="vsgov-step-label">{meta.label}</span>
            </li>
          );
        })}
      </ol>

      {deadlineDays !== null && (
        <div
          className={`vsgov-deadline ${deadlineDays < 0 ? "overdue" : ""}`}
          role="alert"
        >
          <AlarmClock size={16} />
          {deadlineDays < 0 ? (
            <span>
              <strong>OVERDUE</strong> — the board missed the deadline of{" "}
              {formatDateTime(issue.deadline_at)}.
            </span>
          ) : (
            <span>
              Threshold met — the board must convene within{" "}
              <strong>
                {deadlineDays} {deadlineDays === 1 ? "day" : "days"}
              </strong>{" "}
              (by {formatDateTime(issue.deadline_at)}).
            </span>
          )}
        </div>
      )}

      <div className="vsgov-body-card">
        <p>{issue.body}</p>
      </div>

      {issue.discussion_topic_id && (
        <Link
          to={`/discussions/topics/${issue.discussion_topic_id}`}
          className="vsgov-discuss"
        >
          <MessageSquareReply size={16} />
          <span className="vsgov-discuss-label">Discuss this issue</span>
          <span className="vsgov-discuss-count">
            {(issue.discussion?.comment_count ?? 0)}{" "}
            {(issue.discussion?.comment_count ?? 0) === 1 ? "comment" : "comments"}
          </span>
          <span className="vsgov-discuss-arrow">→</span>
        </Link>
      )}

      {issue.close_reason && (
        <div className="vsgov-closed-card">
          <XCircle size={15} />
          <span>
            Closed {issue.closed_at ? formatDateTime(issue.closed_at) : ""} —{" "}
            {issue.close_reason}
          </span>
        </div>
      )}

      {/* Support */}
      {issue.kind !== "QUERY" && (
        <section className="vsgov-section">
          <h2 className="vsgov-section-title">
            <HandHeart size={15} /> Support
          </h2>
          <div className="vsgov-support-card">
            <div className="vsgov-support-top">
              <div className="vsgov-support-count">
                <strong>{issue.support_count}</strong> of{" "}
                <strong>{issue.support_needed}</strong> supports needed ({pct}
                %) · {issue.eligible_members} eligible members
              </div>
              <Button
                variant={issue.my_support ? "secondary" : "primary"}
                size="sm"
                onClick={toggleSupport}
                loading={supportBusy}
                disabled={!supportable && !issue.my_support}
              >
                {issue.my_support ? "Withdraw support" : "Support this issue"}
              </Button>
            </div>
            <div className="vsgov-progress vsgov-progress--lg">
              <div
                className={`vsgov-progress-fill ${pct >= 100 ? "met" : ""}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            {issue.threshold_met_at && (
              <p className="vsgov-field-hint">
                Threshold reached on {formatDateTime(issue.threshold_met_at)}.
              </p>
            )}
            {!supportable && !isTerminal && (
              <p className="vsgov-field-hint">
                Support is recorded while an issue is open; this issue has
                moved on.
              </p>
            )}
          </div>

          <h3 className="vsgov-roster-title">
            Who is supporting ({(issue.supporters || []).length})
          </h3>
          {(issue.supporters || []).length === 0 ? (
            <p className="vsgov-field-hint">
              No supporters yet — be the first to stand behind this.
            </p>
          ) : (
            <ul className="vsgov-roster">
              {issue.supporters.map((s) => (
                <li key={s.id} className="vsgov-roster-row">
                  <Avatar name={s.name} imageUrl={s.avatar_url} size={28} />
                  <span className="vsgov-roster-name">{s.name}</span>
                  <span className="vsgov-roster-when">
                    {formatDateTime(s.supported_at)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {/* Convene the owners' meeting — unlocked once 10% support is reached */}
      {canConvene && (
        <section className="vsgov-section">
          <div className="vsgov-convene-card">
            <div>
              <strong>This request reached the 10% support threshold.</strong>
              <p className="vsgov-field-hint">
                You can now convene the owners' meeting. It starts as a draft —
                set the date, venue and agenda, then issue the notice to send
                the invitation to all members.
              </p>
            </div>
            <Button
              variant="primary"
              size="sm"
              leftIcon={<CalendarPlus size={14} />}
              onClick={() => setConveneOpen(true)}
            >
              Convene owners' meeting
            </Button>
          </div>
        </section>
      )}

      {/* Linked meeting */}
      {issue.meeting && (
        <section className="vsgov-section">
          <h2 className="vsgov-section-title">
            <UsersIcon size={15} /> Linked meeting
          </h2>
          <Link
            to={`/governance/meetings/${issue.meeting.id}`}
            className="vsgov-meeting-link"
          >
            <div className="vsgov-card-pills">
              <Pill
                variant={
                  (MEETING_KIND_META[issue.meeting.kind] || {}).pill || "neutral"
                }
              >
                {(MEETING_KIND_META[issue.meeting.kind] || {}).label ||
                  issue.meeting.kind}
              </Pill>
              <Pill
                variant={
                  (MEETING_STATUS_META[issue.meeting.status] || {}).pill ||
                  "neutral"
                }
              >
                {(MEETING_STATUS_META[issue.meeting.status] || {}).label ||
                  issue.meeting.status}
              </Pill>
            </div>
            <span className="vsgov-meeting-link-title">
              {issue.meeting.title}
            </span>
            <span className="vsgov-card-meta">
              <CalendarDays size={13} />{" "}
              {formatDateTime(issue.meeting.scheduled_at)}
            </span>
          </Link>
        </section>
      )}

      {/* Board answer */}
      {(issue.answer_text || (me.is_board && issue.kind === "QUERY")) && (
        <section className="vsgov-section">
          <h2 className="vsgov-section-title">
            <MessageSquareReply size={15} /> Board's answer
          </h2>
          {issue.answer_text ? (
            <div className="vsgov-answer-card">
              <p>{issue.answer_text}</p>
              {issue.answered_at && (
                <span className="vsgov-answer-when">
                  Answered {formatDateTime(issue.answered_at)}
                </span>
              )}
            </div>
          ) : issue.status !== "CLOSED" ? (
            <div className="vsgov-answer-form">
              <textarea
                className="vsgov-textarea"
                rows={4}
                placeholder="Write the board's formal answer to this query…"
                value={answerText}
                onChange={(e) => setAnswerText(e.target.value)}
              />
              <div className="vsgov-answer-form-foot">
                <span className="vsgov-field-hint">
                  Minimum {ANSWER_MIN} characters. The member is notified when
                  you publish.
                </span>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={submitAnswer}
                  loading={answerBusy}
                  disabled={answerText.trim().length < ANSWER_MIN}
                >
                  Publish answer
                </Button>
              </div>
            </div>
          ) : (
            <p className="vsgov-field-hint">
              This query was closed without a written answer.
            </p>
          )}
        </section>
      )}

      {/* Close action */}
      {canClose && (
        <div className="vsgov-close-row">
          <Button
            variant="destructive"
            size="sm"
            leftIcon={<XCircle size={14} />}
            onClick={() => setCloseOpen(true)}
          >
            {raisedByMe && !me.is_board && !me.is_elder
              ? "Withdraw issue"
              : "Close issue"}
          </Button>
        </div>
      )}

      <TrailTimeline targetId={issue.id} refreshKey={trailKey} />

      <Dialog
        open={closeOpen}
        onOpenChange={setCloseOpen}
        title={
          raisedByMe && !me.is_board && !me.is_elder
            ? "Withdraw this issue"
            : "Close this issue"
        }
        footer={
          <>
            <Button variant="secondary" onClick={() => setCloseOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={submitClose}
              loading={closeBusy}
              disabled={closeReason.trim().length < CLOSE_REASON_MIN}
            >
              Confirm close
            </Button>
          </>
        }
      >
        <div className="vsgov-field" style={{ marginTop: 0 }}>
          <label className="vsgov-label" htmlFor="vsgov-close-reason">
            Reason (recorded in the audit trail)
          </label>
          <textarea
            id="vsgov-close-reason"
            className="vsgov-textarea"
            rows={3}
            placeholder="Why is this issue being closed?"
            value={closeReason}
            onChange={(e) => setCloseReason(e.target.value)}
          />
          <p className="vsgov-field-hint">
            Minimum {CLOSE_REASON_MIN} characters.
          </p>
        </div>
      </Dialog>

      <CreateMeetingDialog
        open={conveneOpen}
        onOpenChange={setConveneOpen}
        kinds={["OWNERS"]}
        initialTitle={`Owners' meeting: ${issue.title}`}
        onCreated={(meeting) => {
          if (meeting?.id) navigate(`/governance/meetings/${meeting.id}`);
        }}
      />
    </div>
  );
}

function BackLink() {
  return (
    <Link to="/governance" className="vsgov-back">
      <ArrowLeft size={14} /> Governance
    </Link>
  );
}
